import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Text, Button, type InputTextProps } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import {
  getAddressComponent,
  GooglePlaceAutocomplete,
} from 'components/shared/GooglePlaceAutocomplete';
import { Row } from '../CommonComponents';
import { type UseProfileFormReturn } from '../useProfileForm';
import { type GooglePlaceDetails } from 'data/v3/data-contracts';
import Steven from 'helpers/steven';
import { useGetGoogleIdAlreadyUsed } from 'data/v3/publicProfiles';
import TrustyAppLockup from 'images/TrustyAppLockup.svg';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xs);
`;

const ValuePropList = styled.ul`
  margin-left: 20px;
  margin-bottom: var(--cc_size_spacing_l);
  padding: 0;
  list-style-type: circle;
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
`;

const TrustyLogo = styled.img`
  display: flex;
  width: 150px;
  padding: 0;
  margin-bottom: var(--cc_size_spacing_l);
`;

const GoogleSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: var(--cc_size_spacing_l);
  border-top: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
`;

const TrustyLink = styled.a`
  color: var(--cc_color_link_primary);
`;

const ValueProps = () => {
  const valuePropMessages = [
    translate('portfolio.profile.valueProp1'),
    translate('portfolio.profile.valueProp2'),
    translate('portfolio.profile.valueProp3'),
  ];

  return (
    <ValuePropList>
      {valuePropMessages.map((message) => (
        <li key={message}>
          <Text
            ccMargin="none none none s"
            size="m"
            family="heading"
            weight="500"
            as="p"
          >
            {message}
          </Text>
        </li>
      ))}
    </ValuePropList>
  );
};

type GoogleSearchStepProps = {
  formMethods: UseProfileFormReturn;
  nextStep: () => void;
};

export const GoogleSearchStepFooterActions = ({
  nextStep,
}: {
  nextStep: () => void;
}) => {
  const handleContinue = () => {
    Steven.elementClicked({
      id: 'create_public_profile_skip_google_place_search',
      content: 'common.continue',
      type: 'button',
      action: 'create_public_profile_skip_google_place_search_clicked',
    });
    nextStep();
  };

  return (
    <Button color="primary" onClick={handleContinue}>
      {translate('common.continue')}
    </Button>
  );
};

export const GoogleSearchStep = ({
  formMethods,
  nextStep,
}: GoogleSearchStepProps) => {
  const [googlePlace, setGooglePlace] = useState<
    GooglePlaceDetails | undefined
  >();
  const [inputMessage, setInputMessage] = useState<{
    message: string;
    status: InputTextProps['status'];
  }>({
    message: '',
    status: 'normal',
  });
  const { data, error } = useGetGoogleIdAlreadyUsed(
    googlePlace?.placeDetails.id,
  );

  const setFormDataFromGoogle = () => {
    const details = googlePlace?.placeDetails;

    formMethods.setValue('name', details.displayName.text);
    formMethods.setValue('googlePlaceId', details.id);
    if (details.websiteUri) {
      formMethods.setValue('website', new URL(details.websiteUri).origin);
    }
    formMethods.setValue('phoneNumber', details.nationalPhoneNumber);

    // address
    formMethods.setValue(
      'addressLine1',
      getAddressComponent(details.addressComponents, [
        'street_number',
        'route',
      ]),
    );
    formMethods.setValue(
      'addressLine2',
      getAddressComponent(details.addressComponents, ['subpremise']),
    );
    formMethods.setValue(
      'city',
      getAddressComponent(details.addressComponents, ['locality']),
    );
    formMethods.setValue(
      'state',
      getAddressComponent(details.addressComponents, [
        'administrative_area_level_1',
      ]),
    );
    formMethods.setValue(
      'postalCode',
      getAddressComponent(details.addressComponents, ['postal_code']),
    );

    Steven.elementClicked({
      id: 'create_public_profile_select_google_place_search',
      content: details.displayName.text,
      type: 'button',
      action: 'create_public_profile_select_google_place_search_clicked',
    });

    nextStep();
  };

  const handlePlaceChanged = (place: GooglePlaceDetails) => {
    setGooglePlace(place);
  };

  useEffect(() => {
    if (data?.data.alreadyUsed) {
      setInputMessage({
        message: translate('portfolio.profile.existingGoogleProfile'),
        status: 'error',
      });
    } else if (data) {
      setFormDataFromGoogle();
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setInputMessage({
        message: translate('common.unexpectedError'),
        status: 'error',
      });
    }
  }, [error]);

  return (
    <Container>
      <TrustyLogo
        src={TrustyAppLockup}
        alt={translate('customers.hypeModal.logoAltText')}
      />
      <div>
        <Text
          as="h1"
          size="xl"
          family="heading"
          weight="800"
          ccMargin="xs none none"
        >
          {translate('portfolio.profile.setupTitle')}
        </Text>
        <Text
          size="m"
          ccMargin="s none m"
          family="heading"
          weight="500"
          as="h2"
        >
          {translate('portfolio.profile.setupSubtitleA')}
          <TrustyLink
            href="https://trusty.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            trusty.app
          </TrustyLink>
          . {translate('portfolio.profile.setupSubtitleB')}
        </Text>
      </div>
      <ValueProps />
      <Row>
        <GoogleSearchContainer>
          <Text as="h3" size="m" family="heading" weight="700">
            {translate('portfolio.profile.googleBusinessSearch')}
          </Text>
          <Text as="p" size="s" family="heading" ccMargin="none none xs">
            {translate('portfolio.profile.googleBusinessSearchMessage')}
          </Text>
        </GoogleSearchContainer>
        <GooglePlaceAutocomplete
          onPlaceSelect={handlePlaceChanged}
          placeholder={translate('portfolio.profile.searchGoogle')}
          message={inputMessage.message}
          status={inputMessage.status}
        />
      </Row>
    </Container>
  );
};
